import React, { Suspense, useEffect } from 'react';
import { useSWRResource } from 'util/resource/useSWRResource';
import useSWR from 'hooks/useSWR';
import Resource from 'util/resource/Resource';
import { useCommunities } from 'apis/CompanyAPI/communities/useCommunities';
import { useCompanies } from 'apis/CompanyAPI/companies/useCompanies';
import { OnboardingWizardStatus, onboardingApiUrls } from 'apis/OnboardingAPI';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import OnboardingWrapper from './Onboarding';
import LoadingBar from 'ui/elements/Loading/LoadingBar';
import ErrorCard from 'ui/views/errors/ErrorCard';
import { useOrganizations } from 'apis/OrganizationAPI/organizations/useOrganizations';
import { useUiState } from 'apis/CompanyAPI/users/useUiState';
import useRoute from 'hooks/useRoute';
import { communityUrls } from 'urls';

export const onboardinWizardStatusKey = onboardingApiUrls.getStatus;

const useOnboardingWizardStatus = () => {
  const swrResult = useSWR<OnboardingWizardStatus>(onboardingApiUrls.getStatus);
  return useSWRResource(swrResult);
};

export default function InitialLoadWrapper({ children }: { children: React.ReactNode }) {
  const { resource: onboardingStatus } = useOnboardingWizardStatus();
  const { uiState: uiStateResource, saveUiState } = useUiState();
  const { push } = useRoute();
  // eager load the following resources. They are used after the onboarding is completed.
  useSelfUserProfile();
  useCompanies();
  useCommunities();
  useOrganizations();

  // redirect to application community first time user loads the app
  useEffect(() => {
    if (uiStateResource.state === 'fetched' && uiStateResource.resource.redirectToCommunity) {
      saveUiState({
        redirectToCommunity: null,
      });
      push(
        uiStateResource.resource.redirectToCommunity.communityCode
          ? communityUrls.join(
              uiStateResource.resource.redirectToCommunity.communitySlug,
              uiStateResource.resource.redirectToCommunity.communityCode,
            )
          : communityUrls.overview(uiStateResource.resource.redirectToCommunity.communitySlug),
      );
    }
  }, [uiStateResource, onboardingStatus.state]);

  return (
    <Suspense fallback={<LoadingBar isComplete={false} />}>
      <Resource
        resource={onboardingStatus}
        renderLoading="Bar"
        renderError={() => (
          <>
            <div className="u-section-padding-y">
              <div className="u-section-padding-top">
                <ErrorCard />
              </div>
            </div>
            {/* Since the layout is currently flex, we need this in place of the right hand sidebar that doesn't load */}
            <div />
          </>
        )}
      >
        {onboardingStatus => {
          if (onboardingStatus.status === 'InProgress') {
            return <OnboardingWrapper onboardingStatus={onboardingStatus} />;
          }

          return children;
        }}
      </Resource>
    </Suspense>
  );
}
